import { useState } from 'react';
import { useGetAllVisitorsQuery, useGetVisitorsByClientIdQuery, useCreateVisitorMutation, useUpdateVisitorMutation, useDeleteVisitorMutation, type Visitor, type VisitorRequest } from '@/api/visitorApi';
import { useGetClientsQuery } from '@/api/clientApi';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Link } from 'react-router-dom';
import { Edit2, Plus, Trash2 } from 'lucide-react';
import { DatePicker } from '@/components/ui/date-picker';
import { useSelector } from "react-redux";
import type { RootState } from "@/store";
import { toast } from "sonner";

const ALL_CLIENTS_VALUE = "ALL";

const US_STATES = [
  "AL", "AK", "AZ", "AR", "CA", "CO", "CT", "DE", "FL", "GA",
  "HI", "ID", "IL", "IN", "IA", "KS", "KY", "LA", "ME", "MD",
  "MA", "MI", "MN", "MS", "MO", "MT", "NE", "NV", "NH", "NJ",
  "NM", "NY", "NC", "ND", "OH", "OK", "OR", "PA", "RI", "SC",
  "SD", "TN", "TX", "UT", "VT", "VA", "WA", "WV", "WI", "WY"
];

const formatSsn = (value: string) => {
  const digits = value.replace(/\D/g, "").slice(0, 9);
  const part1 = digits.slice(0, 3);
  const part2 = digits.slice(3, 5);
  const part3 = digits.slice(5, 9);
  if (digits.length <= 3) return part1;
  if (digits.length <= 5) return `${part1}-${part2}`;
  return `${part1}-${part2}-${part3}`;
};

const VisitorsList = () => {
  const { user } = useSelector((state: RootState) => state.auth);
  const isClientRole = user?.role === "CLIENT" || user?.role === "CLIENT_EMPLOYEE";

  const [selectedClientId, setSelectedClientId] = useState<number | null>(isClientRole ? (user?.clientId ?? null) : null);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<"ALL" | "ACTIVE" | "INACTIVE">("ALL");
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingVisitor, setEditingVisitor] = useState<Visitor | null>(null);
  const [formData, setFormData] = useState<VisitorRequest>({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    dateOfBirth: '',
    clientId: isClientRole ? (user?.clientId ?? 0) : 0,
    clientName: '',
    employeeId: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    gender: '',
    ssn: '',
    status: 'ACTIVE',
  });

  const { data: allVisitors = [], isLoading, refetch } = useGetAllVisitorsQuery(undefined, {
    skip: isClientRole,
  });
  const { data: clientVisitors = [] } = useGetVisitorsByClientIdQuery(selectedClientId || 0, {
    skip: !selectedClientId,
  });
  const { data: clients = [] } = useGetClientsQuery();
  const [createVisitor] = useCreateVisitorMutation();
  const [updateVisitor] = useUpdateVisitorMutation();
  const [deleteVisitor] = useDeleteVisitorMutation();

  const displayedVisitors = (selectedClientId ? clientVisitors : allVisitors)
    .filter((visitor: Visitor) => {
      if (statusFilter !== "ALL" && visitor.status !== statusFilter) return false;
      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase();
        const haystack = [
          visitor.firstName,
          visitor.lastName,
          visitor.email,
          visitor.phone,
          visitor.employeeId,
          visitor.clientName
        ].join(" ").toLowerCase();
        if (!haystack.includes(q)) return false;
      }
      return true;
    })
    .sort((a, b) => {
      const dateA = new Date(a.createdAt).getTime();
      const dateB = new Date(b.createdAt).getTime();
      if (Number.isFinite(dateA) && Number.isFinite(dateB) && dateA !== dateB) {
        return dateB - dateA;
      }
      return b.id - a.id;
    });

  const handleOpenDialog = (visitor?: Visitor) => {
    if (visitor) {
      setEditingVisitor(visitor);
      setFormData({
        firstName: visitor.firstName,
        lastName: visitor.lastName,
        email: visitor.email,
        phone: visitor.phone || '',
        dateOfBirth: visitor.dateOfBirth ? visitor.dateOfBirth.slice(0, 10) : '',
        clientId: visitor.clientId,
        clientName: visitor.clientName,
        employeeId: visitor.employeeId || '',
        address: visitor.address || '',
        city: visitor.city || '',
        state: visitor.state || '',
        zipCode: visitor.zipCode || '',
        gender: visitor.gender || '',
        ssn: visitor.ssn ? formatSsn(visitor.ssn) : '',
        status: visitor.status,
      });
    } else {
      setEditingVisitor(null);
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        dateOfBirth: '',
        clientId: 0,
        clientName: '',
        employeeId: '',
        address: '',
        city: '',
        state: '',
        zipCode: '',
        gender: '',
        ssn: '',
        status: 'ACTIVE',
      });
      if (isClientRole && user?.clientId) {
        setFormData(prev => ({
          ...prev,
          clientId: user.clientId ?? 0,
          clientName: user.clientName || ""
        }));
      }
    }
    setIsDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setEditingVisitor(null);
  };

  const handleSelectClient = (clientId: string) => {
    const client = clients.find(c => c.id === parseInt(clientId));
    if (client) {
      setFormData({
        ...formData,
        clientId: client.id,
        clientName: client.companyName,
      });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (!formData.clientId || formData.clientId <= 0) {
        alert('Please select a client.');
        return;
      }
      if (!formData.dateOfBirth) {
        alert('Date of Birth is required.');
        return;
      }
      if (!formData.gender) {
        alert('Gender is required.');
        return;
      }
      if (!formData.city?.trim() || !formData.state?.trim() || !formData.zipCode?.trim()) {
        alert('City, State, and ZIP Code are required.');
        return;
      }
      if (formData.ssn) {
        const digits = formData.ssn.replace(/\D/g, "");
        if (digits.length !== 9) {
          alert('SSN must have exactly 9 digits (XXX-XX-XXXX).');
          return;
        }
      }
      if (editingVisitor) {
        await updateVisitor({
          id: editingVisitor.id,
          data: formData,
        }).unwrap();
      } else {
        await createVisitor(formData).unwrap();
      }
      handleCloseDialog();
      refetch();
      toast.success(editingVisitor ? 'Visitor updated successfully' : 'Visitor created successfully');
    } catch (error: any) {
      console.error('Error saving visitor:', error);
      const errorMessage = error?.data?.error || error?.data?.message || 'Error saving visitor';
      toast.error(errorMessage);
    }
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('Are you sure you want to delete this visitor?')) {
      try {
        await deleteVisitor(id).unwrap();
        refetch();
        toast.success('Visitor deleted successfully');
      } catch (error: any) {
        console.error('Error deleting visitor:', error);
        const errorMessage = error?.data?.error || error?.data?.message || 'Error deleting visitor';
        toast.error(errorMessage);
      }
    }
  };

  return (
    <div className="space-y-6 p-6">
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold">Visitors Management</h1>
        <Button onClick={() => handleOpenDialog()} className="gap-2">
          <Plus className="w-4 h-4" />
          Add Visitor
        </Button>
      </div>

      <div className="flex flex-col gap-4 md:flex-row md:items-center">
        <Input
          placeholder="Search by name, email, phone, employee ID..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="md:w-72"
        />
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as "ALL" | "ACTIVE" | "INACTIVE")}
          className="h-10 rounded-md border border-slate-300 bg-white px-3 text-sm text-slate-800"
        >
          <option value="ALL">All Status</option>
          <option value="ACTIVE">Active</option>
          <option value="INACTIVE">Inactive</option>
        </select>
        {!isClientRole && (
          <Select
            value={selectedClientId?.toString() ?? ALL_CLIENTS_VALUE}
            onValueChange={(value) =>
              setSelectedClientId(value === ALL_CLIENTS_VALUE ? null : parseInt(value, 10))
            }
          >
            <SelectTrigger className="w-72">
              <SelectValue placeholder="Filter by client..." />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL_CLIENTS_VALUE}>All Clients</SelectItem>
              {clients.map((client: any) => (
                <SelectItem key={client.id} value={client.id.toString()}>
                  {client.companyName}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </div>

      {isLoading ? (
        <div>Loading visitors...</div>
      ) : (
        <div className="rounded-md border overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Phone</TableHead>
                {!isClientRole && <TableHead>Client</TableHead>}
                <TableHead>Employee ID</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {displayedVisitors.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-4">
                    No visitors found
                  </TableCell>
                </TableRow>
              ) : (
                displayedVisitors.map((visitor: Visitor) => (
                  <TableRow key={visitor.id}>
                    <TableCell className="font-medium">
                      {visitor.firstName} {visitor.lastName}
                    </TableCell>
                    <TableCell>{visitor.email}</TableCell>
                    <TableCell>{visitor.phone || '-'}</TableCell>
                    {!isClientRole && <TableCell>{visitor.clientName}</TableCell>}
                    <TableCell>{visitor.employeeId || '-'}</TableCell>
                    <TableCell>
                      <Badge variant={visitor.status === 'ACTIVE' ? 'default' : 'secondary'}>
                        {visitor.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right space-x-2">
                      <Button variant="outline" size="sm" asChild>
                        <Link to={isClientRole ? `/client/visitors/${visitor.id}/orders` : `/super-admin/visitors/${visitor.id}/orders`}>
                          View Orders
                        </Link>
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleOpenDialog(visitor)}
                      >
                        <Edit2 className="w-4 h-4" />
                      </Button>
                      {!isClientRole && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDelete(visitor.id)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      )}

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingVisitor ? 'Edit Visitor' : 'Add New Visitor'}
            </DialogTitle>
            <DialogDescription>
              {editingVisitor
                ? 'Update visitor information'
                : 'Create a new visitor record'}
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium">First Name *</label>
                <Input
                  value={formData.firstName}
                  onChange={(e) =>
                    setFormData({ ...formData, firstName: e.target.value })
                  }
                  required
                />
              </div>
              <div>
                <label className="text-sm font-medium">Last Name *</label>
                <Input
                  value={formData.lastName}
                  onChange={(e) =>
                    setFormData({ ...formData, lastName: e.target.value })
                  }
                  required
                />
              </div>
            </div>

            <div>
              <label className="text-sm font-medium">Email *</label>
              <Input
                type="email"
                value={formData.email}
                onChange={(e) =>
                  setFormData({ ...formData, email: e.target.value })
                }
                required
              />
            </div>

            <div>
              <label className="text-sm font-medium">Phone</label>
              <Input
                value={formData.phone || ''}
                onChange={(e) =>
                  setFormData({ ...formData, phone: e.target.value })
                }
              />
            </div>

            <div>
              <label className="text-sm font-medium">Date of Birth *</label>
              <div className="mt-1">
                <DatePicker
                  value={formData.dateOfBirth}
                  onChange={(date: string) => setFormData({ ...formData, dateOfBirth: date })}
                  label="Select Date of Birth"
                />
              </div>
            </div>

            <div>
              <label className="text-sm font-medium">Client *</label>
              <Select
                value={formData.clientId && formData.clientId > 0 ? formData.clientId.toString() : ""}
                onValueChange={handleSelectClient}
                disabled={isClientRole}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select client" />
                </SelectTrigger>
                <SelectContent>
                  {clients.length === 0 && (
                    <SelectItem value="NO_CLIENTS" disabled>
                      No clients available
                    </SelectItem>
                  )}
                  {clients.map((client: any) => (
                    <SelectItem key={client.id} value={client.id.toString()}>
                      {client.companyName}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="text-sm font-medium">Address</label>
              <Input
                value={formData.address || ''}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
              />
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="text-sm font-medium">City *</label>
                <Input
                  value={formData.city || ''}
                  onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                  required
                />
              </div>
              <div>
                <label className="text-sm font-medium">State *</label>
                <Select
                  value={formData.state || ""}
                  onValueChange={(value) => setFormData({ ...formData, state: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="State" />
                  </SelectTrigger>
                  <SelectContent>
                    {US_STATES.map((state) => (
                      <SelectItem key={state} value={state}>
                        {state}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium">ZIP Code *</label>
                <Input
                  value={formData.zipCode || ''}
                  onChange={(e) => setFormData({ ...formData, zipCode: e.target.value })}
                  required
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium">Gender *</label>
                <Select
                  value={formData.gender || 'UNSPECIFIED'}
                  onValueChange={(value) =>
                    setFormData({ ...formData, gender: value === 'UNSPECIFIED' ? '' : value })
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select gender" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="UNSPECIFIED">Not specified</SelectItem>
                    <SelectItem value="MALE">Male</SelectItem>
                    <SelectItem value="FEMALE">Female</SelectItem>
                    <SelectItem value="OTHER">Other</SelectItem>
                    <SelectItem value="PREFER_NOT_TO_SAY">Prefer not to say</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium">SSN (Optional)</label>
                <Input
                  value={formData.ssn || ''}
                  onChange={(e) => setFormData({ ...formData, ssn: formatSsn(e.target.value) })}
                  placeholder="XXX-XX-XXXX"
                  maxLength={11}
                />
              </div>
            </div>

            <div>
              <label className="text-sm font-medium">Status *</label>
              <Select
                value={formData.status}
                onValueChange={(value) =>
                  setFormData({ ...formData, status: value })
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ACTIVE">Active</SelectItem>
                  <SelectItem value="INACTIVE">Inactive</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={handleCloseDialog}
              >
                Cancel
              </Button>
              <Button type="submit">
                {editingVisitor ? 'Update' : 'Create'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default VisitorsList;
